import { useState, useEffect, useRef } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Phone, MapPin, CheckCircle, ArrowRight, Shield, Clock, Award, DollarSign, CreditCard, Building2, Home, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCTA from "@/components/MobileCTA";
import StickyCtaBar from "@/components/StickyCtaBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceAreaPage = ({ 
  areaName, 
  areaSlug,
  metaDescription,
  heroTitle,
  heroSubtitle,
  areaDescription,
  localLandmarks,
  neighborhoods,
  uniqueContent,
  testimonial,
  extendedContent
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    stump_count: 1,
    stump_size: "",
    message: "",
  });
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // All service areas for internal linking
  const allServiceAreas = [
    { name: "Tucson", slug: "/tucson-stump-grinding" },
    { name: "Oro Valley", slug: "/oro-valley-stump-grinding" },
    { name: "Catalina", slug: "/catalina-stump-grinding" },
    { name: "Sahuarita", slug: "/sahuarita-stump-grinding" },
    { name: "SaddleBrooke", slug: "/saddlebrooke-stump-grinding" },
    { name: "Oracle", slug: "/oracle-stump-grinding" },
  ];

  const otherAreas = allServiceAreas.filter(area => area.slug !== `/${areaSlug}`);

  const pageTitle = areaName + " Stump Grinding | Stump Removal " + areaName + " AZ | Stump Grinding Service";
  const ogTitle = areaName + " Stump Grinding | Stump Removal Service Near Me";
  const canonicalUrl = "https://tucsonstumpgrinding.com/" + areaSlug;

  useSEO({
    title: pageTitle,
    canonical: canonicalUrl,
    description: metaDescription,
    ogTitle: ogTitle,
    ogDescription: metaDescription,
    ogUrl: canonicalUrl,
  });

  // Schema data for this service area
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://tucsonstumpgrinding.com/${areaSlug}#localbusiness`,
    "name": `Tucson Stump Grinding - ${areaName} Tree Stump Removal Service`,
    "description": `Professional stump grinding and tree stump removal service in ${areaName}, Arizona. Residential and commercial properties. $300-$850 pricing, financing available.`,
    "url": `https://tucsonstumpgrinding.com/${areaSlug}`,
    "telephone": "+1-520-200-7739",
    "priceRange": "$300-$850",
    "image": "https://tucsonstumpgrinding.com/logo.png",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": areaName,
      "addressRegion": "AZ",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": areaName === "Tucson" ? "32.2226" : areaName === "Oro Valley" ? "32.3909" : areaName === "Catalina" ? "32.5050" : areaName === "Sahuarita" ? "31.9576" : areaName === "SaddleBrooke" ? "32.5361" : "32.6101",
      "longitude": areaName === "Tucson" ? "-110.9747" : areaName === "Oro Valley" ? "-110.9665" : areaName === "Catalina" ? "-110.9190" : areaName === "Sahuarita" ? "-110.9554" : areaName === "SaddleBrooke" ? "-110.8751" : "-110.7676"
    },
    "areaServed": {
      "@type": "City",
      "name": areaName,
      "containedInPlace": {
        "@type": "State",
        "name": "Arizona"
      }
    },
    "serviceType": ["Stump Grinding", "Tree Stump Removal", "Root Grinding", "Commercial Stump Removal", "Residential Stump Grinding"],
    "paymentAccepted": ["Cash", "Credit Card", "Visa", "Mastercard", "American Express", "Discover"],
    "currenciesAccepted": "USD",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "14:00"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127"
    }
  };

  // Inject schema JSON-LD into document head
  useEffect(() => {
    const existingSchema = document.getElementById(`schema-${areaSlug}`);
    if (existingSchema) existingSchema.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `schema-${areaSlug}`;
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(`schema-${areaSlug}`);
      if (el) el.remove();
    };
  }, [areaSlug, schemaData]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      ['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type)
    );
    
    if (validFiles.length + images.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('address', formData.address);
      submitData.append('service_type', formData.service_type);
      submitData.append('stump_count', formData.stump_count.toString());
      submitData.append('stump_size', formData.stump_size);
      submitData.append('message', `[From ${areaName} page] ${formData.message}`);
      
      images.forEach((img) => {
        submitData.append('images', img.file);
      });

      const response = await fetch(`${API}/quotes-with-images`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) throw new Error('Failed to submit');

      toast.success("Quote request submitted! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "",
        stump_count: 1,
        stump_size: "",
        message: "",
      });
      setImages([]);
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 md:pt-24 pb-16 lg:pb-24 overflow-hidden" id="top">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.pexels.com/photos/13790935/pexels-photo-13790935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
            }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 noise-bg" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="text-white animate-slide-in-left">
                <div className="accent-line mb-6" />
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight uppercase">
                  {heroTitle}
                </h1>
                <p className="font-body text-lg sm:text-xl text-gray-300 mb-6">
                  {heroSubtitle}
                </p>

                {/* Pricing & Financing */}
                <div className="bg-[#FF4D00]/20 border-2 border-[#FF4D00] p-4 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-6 h-6 text-[#FF4D00]" />
                    <span className="font-heading font-bold text-xl text-white">$300 - $850</span>
                  </div>
                  <p className="font-body text-gray-300 text-sm">
                    Pricing varies based on stump size, accessibility, and quantity.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <CreditCard className="w-4 h-4 text-[#FF4D00]" />
                    <span className="font-body text-[#FF4D00] font-semibold text-sm">Financing Available!</span>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {[
                    { icon: Shield, text: "Licensed & Insured" },
                    { icon: Clock, text: "Same-Day Service*" },
                    { icon: Award, text: "10+ Years Experience" },
                  ].map((badge, index) => (
                    <div key={index} className="trust-badge rounded-sm">
                      <badge.icon className="w-4 h-4 text-[#FF4D00]" />
                      <span className="font-body text-sm font-medium">{badge.text}</span>
                    </div>
                  ))}
                </div>

                {/* Residential & Commercial */}
                <div className="flex gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-[#FF4D00]" />
                    <span className="font-body text-gray-300 text-sm">Residential</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#FF4D00]" />
                    <span className="font-body text-gray-300 text-sm">Commercial</span>
                  </div>
                </div>

                <p className="font-body text-xs text-gray-400 mb-6">
                  * Same-day stump grinding available when scheduled with a current tree removal service.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    className="btn-primary px-6 py-4 text-base rounded-none flex items-center gap-2"
                  >
                    <a href="tel:+15202007739,2">
                      Call: (520) 200-7739 Ext 2
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right - Quote Form */}
              <div className="quote-form p-6 sm:p-8 animate-fade-in-up">
                <div className="text-center mb-6">
                  <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] uppercase">
                    Free {areaName} Quote
                  </h2>
                  <p className="font-body text-gray-600 mt-2 text-sm">
                    Residential & Commercial • Financing available
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="font-body font-semibold text-sm">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 rounded-none"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-body font-semibold text-sm">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-1 rounded-none"
                        placeholder="(520) 555-1234"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-body font-semibold text-sm">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="font-body font-semibold text-sm">Property Address *</Label>
                    <Input
                      id="address"
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder={`123 Main St, ${areaName}, AZ`}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body font-semibold text-sm">Service Needed *</Label>
                      <Select
                        value={formData.service_type}
                        onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                      >
                        <SelectTrigger className="mt-1 rounded-none">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stump-grinding">Stump Grinding</SelectItem>
                          <SelectItem value="stump-removal">Complete Removal</SelectItem>
                          <SelectItem value="root-grinding">Root Grinding</SelectItem>
                          <SelectItem value="commercial">Commercial Project</SelectItem>
                          <SelectItem value="multiple">Multiple Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-body font-semibold text-sm">Number of Stumps</Label>
                      <Select
                        value={formData.stump_count.toString()}
                        onValueChange={(value) => setFormData({ ...formData, stump_count: parseInt(value) })}
                      >
                        <SelectTrigger className="mt-1 rounded-none">
                          <SelectValue placeholder="Select count" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Stump</SelectItem>
                          <SelectItem value="2">2 Stumps</SelectItem>
                          <SelectItem value="3">3 Stumps</SelectItem>
                          <SelectItem value="4">4 Stumps</SelectItem>
                          <SelectItem value="5">5+ Stumps</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <Label className="font-body font-semibold text-sm">Upload Photos (Optional)</Label>
                    <div 
                      className="mt-1 border-2 border-dashed border-gray-300 rounded-sm p-3 text-center cursor-pointer hover:border-[#FF4D00] transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="font-body text-xs text-gray-600">Click to upload (max 5)</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic"
                      multiple
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    {images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {images.map((img, index) => (
                          <div key={index} className="relative">
                            <img src={img.preview} alt={`Preview ${index + 1}`} className="w-10 h-10 object-cover rounded border" />
                            <button type="button" onClick={() => removeImage(index)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center">
                              <X className="w-2 h-2" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-body font-semibold text-sm">Details</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="Tell us about your stumps..."
                      rows={2}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-4 rounded-none"
                  >
                    {isSubmitting ? "Submitting..." : "Get My Free Quote"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Area Description Section */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="accent-line mb-4" />
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A] uppercase mb-6">
                  Professional Tree Stump Removal Service in {areaName}, Arizona
                </h2>
                <div className="font-body text-gray-700 space-y-4 leading-relaxed">
                  {areaDescription.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                {/* Commercial Services Banner */}
                <div className="mt-8 bg-[#1A1A1A] p-6 relative overflow-hidden noise-bg">
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-3">
                      <Building2 className="w-10 h-10 text-[#FF4D00]" />
                      <div>
                        <h3 className="font-heading font-bold text-white uppercase text-lg">Commercial Stump Grinding</h3>
                        <p className="font-body text-gray-400 text-sm">
                          Serving {areaName} businesses, HOAs, property managers & contractors
                        </p>
                      </div>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-2 mt-4">
                      {["Shopping centers", "Office parks", "Apartment complexes", "New construction sites", "HOA communities", "Municipal properties"].map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#FF4D00]" />
                          <span className="font-body text-sm text-gray-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Financing Banner */}
                <div className="mt-6 bg-[#FF4D00]/10 border-2 border-[#FF4D00] p-6">
                  <div className="flex items-center gap-4">
                    <CreditCard className="w-10 h-10 text-[#FF4D00]" />
                    <div>
                      <h3 className="font-heading font-bold text-[#1A1A1A] uppercase text-lg">Financing Available</h3>
                      <p className="font-body text-gray-600 text-sm">
                        Affordable payment plans for {areaName} residential and commercial customers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {/* Services in this area */}
                <div className="bg-[#F5F5F0] border-2 border-[#E5E5E0] p-6 mb-6">
                  <h3 className="font-heading text-xl font-bold text-[#1A1A1A] uppercase mb-4">
                    Our {areaName} Services
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Residential Stump Grinding",
                      "Commercial Stump Removal",
                      "Root Grinding & Removal",
                      "Land Clearing",
                      "Same-Day Emergency Service",
                      "Multi-Property Discounts",
                    ].map((service, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                        <span className="font-body text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Neighborhoods */}
                {neighborhoods && neighborhoods.length > 0 && (
                  <div className="bg-white border-2 border-[#1A1A1A] p-6">
                    <h3 className="font-heading text-xl font-bold text-[#1A1A1A] uppercase mb-4">
                      Neighborhoods We Serve in {areaName}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {neighborhoods.map((neighborhood, index) => (
                        <span
                          key={index}
                          className="bg-[#F5F5F0] px-3 py-1 font-body text-sm text-gray-700 border border-[#E5E5E0]"
                        >
                          {neighborhood}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Extended Content Section for SEO (800+ words) */}
        {extendedContent && (
          <section className="py-16 lg:py-24 bg-[#F5F5F0]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase mb-8">
                {extendedContent.title}
              </h2>
              <div className="font-body text-gray-700 space-y-6 leading-relaxed">
                {extendedContent.sections.map((section, index) => (
                  <div key={index}>
                    {section.heading && (
                      <h3 className="font-heading text-xl font-bold text-[#1A1A1A] uppercase mb-3 mt-6">
                        {section.heading}
                      </h3>
                    )}
                    {section.paragraphs.map((para, pIndex) => (
                      <p key={pIndex} className="mb-4">{para}</p>
                    ))}
                  </div>
                ))}
              </div>

              {/* Internal Links for SEO */}
              <div className="mt-10 pt-8 border-t-2 border-[#E5E5E0]">
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A] uppercase mb-4">
                  Stump Grinding Services Throughout Southern Arizona
                </h3>
                <div className="font-body text-gray-700 leading-relaxed space-y-4">
                  <p>
                    In addition to {areaName}, Tucson Stump Grinding provides professional tree stump removal
                    for residential and commercial properties across Southern Arizona. Our experienced crew and
                    commercial-grade equipment deliver reliable results in every community we serve.
                  </p>
                  <p>
                    We proudly serve homeowners, businesses, HOAs, and property managers in{" "}
                    {otherAreas.map((area, i) => (
                      <span key={area.slug}>
                        {i > 0 && i < otherAreas.length - 1 && ", "}
                        {i === otherAreas.length - 1 && ", and "}
                        <Link to={area.slug} className="text-[#FF4D00] hover:underline font-semibold">
                          {area.name}
                        </Link>
                      </span>
                    ))}
                    . Each community has unique landscaping challenges, and our decade of local experience means
                    we understand the specific stump grinding needs of every area we serve.
                  </p>
                  <p>
                    Need a tree removed before stump grinding? Our{" "}
                    <Link to="/tree-removal" className="text-green-600 hover:underline font-semibold">
                      tree removal services
                    </Link>{" "}
                    handle the complete job from tree removal to stump grinding in a single visit. Call{" "}
                    <a href="tel:+15202007739,2" className="text-[#FF4D00] font-semibold">(520) 200-7739 Ext 2</a>{" "}
                    for a free quote on any stump grinding or tree stump removal project in {areaName} or
                    anywhere in Southern Arizona.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Unique Content Section */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase mb-6">
                {uniqueContent.title}
              </h2>
              <div className="font-body text-gray-700 space-y-4 leading-relaxed">
                {uniqueContent.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {testimonial && (
          <section className="py-16 bg-[#1A1A1A] relative overflow-hidden noise-bg">
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="text-[#FF4D00] text-6xl font-serif mb-4">"</div>
              <p className="font-body text-xl text-white mb-6 italic">
                {testimonial.text}
              </p>
              <div>
                <div className="font-heading font-bold text-[#FF4D00] uppercase">
                  {testimonial.author}
                </div>
                <div className="font-body text-gray-400 text-sm">
                  {testimonial.location}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Pricing Section */}
        <section className="py-16 lg:py-24 bg-[#F5F5F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="accent-line mx-auto mb-4" />
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A] uppercase mb-4">
                {areaName} Stump Grinding Pricing
              </h2>
              <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
                Transparent, competitive pricing for {areaName} residential and commercial properties.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border-2 border-[#E5E5E0] p-6 text-center">
                <h3 className="font-heading text-lg font-bold text-[#1A1A1A] uppercase mb-2">Small Stumps</h3>
                <p className="font-body text-sm text-gray-600 mb-4">Under 12" diameter</p>
                <div className="font-heading text-3xl font-bold text-[#FF4D00]">$300-$400</div>
              </div>
              <div className="bg-[#1A1A1A] border-2 border-[#FF4D00] p-6 text-center relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF4D00] px-3 py-1 font-heading text-xs font-bold text-white uppercase">
                  Most Common
                </div>
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-2">Medium Stumps</h3>
                <p className="font-body text-sm text-gray-400 mb-4">12" - 24" diameter</p>
                <div className="font-heading text-3xl font-bold text-[#FF4D00]">$400-$600</div>
              </div>
              <div className="bg-white border-2 border-[#E5E5E0] p-6 text-center">
                <h3 className="font-heading text-lg font-bold text-[#1A1A1A] uppercase mb-2">Large Stumps</h3>
                <p className="font-body text-sm text-gray-600 mb-4">24"+ diameter</p>
                <div className="font-heading text-3xl font-bold text-[#FF4D00]">$600-$850</div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="font-body text-gray-600 mb-4">
                * Commercial projects and multi-stump discounts available. Contact us for custom quotes.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/10 border border-[#FF4D00] px-6 py-3">
                <CreditCard className="w-5 h-5 text-[#FF4D00]" />
                <span className="font-heading font-bold text-[#1A1A1A] uppercase">Financing Available</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase mb-4">
              Ready to Remove Your {areaName} Stumps?
            </h2>
            <p className="font-body text-white/90 text-lg mb-8">
              Residential & commercial. Same-day service available. Financing options for every budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-[#1A1A1A] text-white hover:bg-black px-8 py-4 text-lg rounded-none font-heading font-bold uppercase tracking-wide"
              >
                <a href="tel:+15202007739,2">Call (520) 200-7739 Ext 2</a>
              </Button>
              <Button
                asChild
                className="bg-white text-[#1A1A1A] hover:bg-gray-100 px-8 py-4 text-lg rounded-none font-heading font-bold uppercase tracking-wide"
              >
                <a href="#top">Get Free Quote</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Other Service Areas - Internal Links */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase mb-4">
                Tree Stump Removal Service in Other Areas
              </h2>
              <p className="font-body text-gray-600">
                We provide professional stump grinding throughout Southern Arizona. Residential and commercial services available.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {otherAreas.map((area) => (
                <Link
                  key={area.slug}
                  to={area.slug}
                  className="bg-[#F5F5F0] border-2 border-[#E5E5E0] p-4 text-center hover:border-[#FF4D00] hover:bg-white transition-all group"
                >
                  <div className="flex flex-col items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#FF4D00]" />
                    <span className="font-heading font-bold text-[#1A1A1A] uppercase text-sm group-hover:text-[#FF4D00] transition-colors">
                      {area.name}, AZ
                    </span>
                    <span className="font-body text-xs text-gray-500">
                      Stump Grinding
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Tree Removal Link */}
            <div className="mt-8 text-center">
              <p className="font-body text-gray-600 mb-4">Need a tree removed first?</p>
              <Link
                to="/tree-removal"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 font-heading font-bold uppercase text-sm tracking-wide transition-colors"
              >
                Tree Removal Services →
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileCTA />
      <StickyCtaBar />
    </div>
  );
};

export default ServiceAreaPage;
